import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Modal, Form, Button, Input, Label, Header } from 'semantic-ui-react';

import CloseableModal from 'components/common/CloseableModal';
import { QuestionGroupType, QuestionType } from 'components/modules/questions/types';

import { validateGroupName } from './GroupEditModal.utils';
import AllQuestionsAssignedToGroup from './components/AllQuestionsAssignedToGroup';
import AvailableQuestionsList from './components/AvailableQuestionsList';
import GroupQuestions from './components/GroupQuestions';
import NoQuestionsAssignedToGroup from './components/NoQuestionsAssignedToGroup';

import './GroupEditModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  group: QuestionGroupType,
  allQuestions: PropTypes.arrayOf(QuestionType).isRequired,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const defaultProps = {
  group: null,
  saving: false,
};

export default function GroupEditModal({
  open,
  group,
  allQuestions,
  saving,
  onClose: onCloseProp,
  onSave: onSaveProp,
}) {
  const [groupName, setGroupName] = useState('');
  const [groupQuestions, setGroupQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (group) {
        setGroupName(group.name || '');

        const questionsInGroup = (group.questions || [])
          .map((gq) => allQuestions.find((q) => q.id === gq.id))
          .filter(Boolean);
        setGroupQuestions(questionsInGroup);

        const questionsNotInGroup = allQuestions.filter(
          (question) => !(group.questions || []).some((gq) => gq.id === question.id),
        );
        setAvailableQuestions(questionsNotInGroup);
      } else {
        setGroupName('');
        setGroupQuestions([]);
        setAvailableQuestions(allQuestions);
      }

      setErrors({});
    }
  }, [open, group, allQuestions]);

  function onGroupNameChange(e, { value }) {
    setGroupName(value);

    if (errors.groupName) {
      setErrors((prev) => ({ ...prev, groupName: null }));
    }
  }

  function onAddQuestion(questionId) {
    const questionToAdd = availableQuestions.find((q) => q.id === questionId);
    if (questionToAdd) {
      setGroupQuestions((prev) => [...prev, questionToAdd]);
      setAvailableQuestions((prev) => prev.filter((q) => q.id !== questionId));
    }
  }

  function onRemoveQuestion(questionId) {
    const questionToRemove = groupQuestions.find((q) => q.id === questionId);
    if (questionToRemove) {
      setAvailableQuestions((prev) => [...prev, questionToRemove]);
      setGroupQuestions((prev) => prev.filter((q) => q.id !== questionId));
    }
  }

  function onQuestionSortEnd({ oldIndex, newIndex }) {
    if (oldIndex === newIndex) return;

    const reorderedQuestions = [...groupQuestions];
    const [movedQuestion] = reorderedQuestions.splice(oldIndex, 1);
    reorderedQuestions.splice(newIndex, 0, movedQuestion);

    setGroupQuestions(reorderedQuestions);
  }

  async function onSaveClick() {
    const nameError = validateGroupName(groupName);

    if (nameError) {
      setErrors({ groupName: nameError });
      return;
    }

    const updatedGroup = {
      ...group,
      name: groupName.trim(),
    };

    await onSaveProp(updatedGroup, groupQuestions);
  }

  function onCancelClick() {
    if (saving) return;
    setErrors({});
    onCloseProp();
  }

  return (
    <CloseableModal
      open={open}
      onClose={onCancelClick}
      header={group ? 'Edit Group' : 'Create New Group'}
      size="large"
      className="group-edit-modal"
      closeIconDisabled={saving}
    >
      <Modal.Content scrolling>
        <Form loading={saving}>
          <Form.Field error={!!errors.groupName}>
            <label htmlFor="group-name">Group Name</label>
            <Input
              id="group-name"
              value={groupName}
              onChange={onGroupNameChange}
              placeholder="Enter group name..."
              maxLength={50}
            />
            {errors.groupName && (
              <Label pointing color="red">
                {errors.groupName}
              </Label>
            )}
          </Form.Field>

          <div className="questions-section">
            <Header as="h5">Questions in this Group ({groupQuestions.length})</Header>
            {groupQuestions.length === 0 && <NoQuestionsAssignedToGroup />}
            {groupQuestions.length > 0 && (
              <GroupQuestions
                questions={groupQuestions}
                onRemove={onRemoveQuestion}
                onSortEnd={onQuestionSortEnd}
              />
            )}
          </div>

          <div className="available-questions-section">
            <Header as="h5">Available Questions ({availableQuestions.length})</Header>
            {availableQuestions.length === 0 && <AllQuestionsAssignedToGroup />}
            {availableQuestions.length > 0 && (
              <AvailableQuestionsList questions={availableQuestions} onAdd={onAddQuestion} />
            )}
          </div>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button
          className="action-button-gray"
          content="Cancel"
          onClick={onCancelClick}
          disabled={saving}
        />
        <Button
          className="secondary-navy"
          content={group ? 'Save Changes' : 'Create Group'}
          onClick={onSaveClick}
          loading={saving}
          disabled={!!errors.groupName || saving}
        />
      </Modal.Actions>
    </CloseableModal>
  );
}

GroupEditModal.propTypes = propTypes;
GroupEditModal.defaultProps = defaultProps;
